import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NewbornIPPMedicationsTherapiesQuestionsLinkIds } from "@/encounters/constants/newborn-ipp";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";
import { pascalCase } from "@/utils/strings";

const MedicationAndTherapiesDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornIPPMedicationAndTherapies,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousSizeAnswers = getQuestionnaireAnswers(previousVersion);
  const currentSizeAnswers = getQuestionnaireAnswers(currentVersion);

  const previousEyeProphylaxis =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.EyeProphylaxis]?.valueString;
  const currentEyeProphylaxis =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.EyeProphylaxis]?.valueString;
  const previousVitaminK =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminK]?.valueString;
  const currentVitaminK =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminK]?.valueString;
  const previousVitaminKDose =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminKDose]?.valueString;
  const currentVitaminKDose =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminKDose]?.valueString;
  const previousVitaminKLotNumber =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminKLotNumber]
      ?.valueString;
  const currentVitaminKLotNumber =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminKLotNumber]
      ?.valueString;
  const previousHepatitisB =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.HepatitisB]?.valueString;
  const currentHepatitisB =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.HepatitisB]?.valueString;
  const previousEyeProphylaxisNotes =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.EyeProphylaxisNotes]
      ?.valueString;
  const currentEyeProphylaxisNotes =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.EyeProphylaxisNotes]
      ?.valueString;
  const previousVitaminKNotes =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminKNotes]?.valueString;
  const currentVitaminKNotes =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.VitaminKNotes]?.valueString;
  const previousHepatitisBNotes =
    previousSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.HepatitisBNotes]
      ?.valueString;
  const currentHepatitisBNotes =
    currentSizeAnswers[NewbornIPPMedicationsTherapiesQuestionsLinkIds.HepatitisBNotes]?.valueString;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Medication & Therapies</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Eye Prophylaxis</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={pascalCase(currentEyeProphylaxis as string)}
                  previous={pascalCase(previousEyeProphylaxis as string)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Eye Prophylaxis Notes</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentEyeProphylaxisNotes}
                  previous={previousEyeProphylaxisNotes}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={pascalCase(currentVitaminK as string)}
                  previous={pascalCase(previousVitaminK as string)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K Dose</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentVitaminKDose}
                  previous={previousVitaminKDose}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K Lot Number</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentVitaminKLotNumber}
                  previous={previousVitaminKLotNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K Notes</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentVitaminKNotes}
                  previous={previousVitaminKNotes}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Hepatitis B</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={pascalCase(currentHepatitisB as string)}
                  previous={pascalCase(previousHepatitisB as string)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Hepatitis B Notes</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentHepatitisBNotes}
                  previous={previousHepatitisBNotes}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Eye Prophylaxis</TextWrapper>
                <WordsDiff
                  current={pascalCase(currentEyeProphylaxis as string)}
                  previous={pascalCase(previousEyeProphylaxis as string)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Eye Prophylaxis Notes</TextWrapper>
                <WordsDiff
                  current={currentEyeProphylaxisNotes}
                  previous={previousEyeProphylaxisNotes}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K</TextWrapper>
                <WordsDiff
                  current={pascalCase(currentVitaminK as string)}
                  previous={pascalCase(previousVitaminK as string)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K Dose</TextWrapper>
                <WordsDiff current={currentVitaminKDose} previous={previousVitaminKDose} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K Lot Number</TextWrapper>
                <WordsDiff
                  current={currentVitaminKLotNumber}
                  previous={previousVitaminKLotNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Vitamin K Notes</TextWrapper>
                <WordsDiff current={currentVitaminKNotes} previous={previousVitaminKNotes} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Hepatitis B</TextWrapper>
                <WordsDiff
                  current={pascalCase(currentHepatitisB as string)}
                  previous={pascalCase(previousHepatitisB as string)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Hepatitis B Notes</TextWrapper>
                <WordsDiff current={currentHepatitisBNotes} previous={previousHepatitisBNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default MedicationAndTherapiesDiff;
